import { toJS } from 'mobx'
import React, { useRef } from 'react'
import { FormContextProvider, createFormContext } from '../Providers/FormContextProvider'
import { FormField } from './FormField'

interface FormProps {
    children?: React.ReactNode | ((formStore: any) => React.ReactNode)
    formContext?: any
    isLanguageSwitcher?: boolean
    name?: string
    initialValues?: any
    autoComplete?: string
    [key: string]: any
}

const FormTemplate: React.FC<{
    children?: React.ReactNode
    onSubmit?: (values: Record<string, any>) => void | Promise<void>
    isLanguageSwitcher?: boolean
    autoComplete?: string
}> = ({ children, onSubmit, isLanguageSwitcher = true, autoComplete = 'on' }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (onSubmit) {
            onSubmit({})
        }
    }

    return (
        <div className="form-container">
            {/* <LanguageSwitcher isLanguageSwitcher={isLanguageSwitcher} /> */}
            <form onSubmit={handleSubmit} autoComplete={autoComplete}>{children}</form>
        </div>
    )
}

export const Form: React.FC<FormProps> = ({ children, formContext: externalFormContext, isLanguageSwitcher = true, ...props }) => {
    const formContextRef = useRef<any>(null)

    let formContext: any
    if (externalFormContext) {
        formContext = externalFormContext
        formContext.mergeToInstance({ ...props })
    } else {
        if (!formContextRef.current) {
            formContextRef.current = createFormContext({
                children,
                ...props,
            })
        } else {
            formContextRef.current.mergeToInstance({ ...props })
        }
        formContext = formContextRef.current
    }

    if (!children && formContext.fields && formContext.fields.size > 0) {
        const children: React.ReactElement[] = []

        try {
            const fields = toJS(formContext.fields)

            if (fields instanceof Map) {
                // Обработка Map объектов (новая архитектура)
                const keys = Array.from(fields.keys()) as string[]

                keys.forEach((key: string) => {
                    children.push(<FormField key={key} name={key} />)
                })
            } else if (fields && typeof fields === 'object') {
                // Обработка обычных объектов
                Object.keys(fields as any).forEach((key) => {
                    children.push(<FormField key={key} name={key} />)
                })
            }
        } catch (error) {
            console.warn('Error rendering form fields:', error)
        }
    }

    // Обробник submit, який валідує та передає значення в onSubmit
    const handleFormSubmit = async (values: Record<string, any>) => {
        // Валідація форми
        const isValid = formContext.validate()

        if (!isValid) {
            // Виводимо помилки валідації у зрозумілому форматі
            const errors = toJS(formContext.errors)
            console.warn('Form validation failed:', errors)
            console.table(errors) // Таблична форма для кращого читання
            return
        }

        // Викликаємо onSubmit з фактичними значеннями форми
        if (props.onSubmit) {
            await props.onSubmit({ values: toJS(formContext.values) })
        }
    }

    formContext.mergeToInstance({
        template: FormTemplate,
        children,
        props: {
            onSubmit: handleFormSubmit,
            isLanguageSwitcher,
            autoComplete: props.autoComplete ?? 'on',
        },
        debug: {
            id: `form-${formContext.name || 'unnamed'}`,
            name: `Form: ${formContext.name || 'Unnamed'}`,
            type: 'component' as const,
            path: '/Core/UI/Form/Components/Form.tsx',
            enabled: (import.meta as any).env?.DEV,
        },
    })

    return <FormContextProvider value={formContext}>{formContext.render()}</FormContextProvider>
}

export default Form
